import { defineStore } from "pinia";
import type { CallStatus, CallState, VideoProviderType } from "@virtual-care/shared";

export interface ExtendedCallState extends CallState {
  provider?: VideoProviderType;
  credentials?: any;
}

export const useCallStore = defineStore("call", {
  state: (): ExtendedCallState => ({
    sessionId: null,
    status: "idle",
    roomId: null,
    initiatorId: null,
    isVideoEnabled: true,
    isAudioEnabled: true,
    connectionTime: undefined,
    error: undefined,
    provider: undefined,
    credentials: undefined,
  }),

  getters: {
    isInCall: (state): boolean => state.status === "connected",
    isConnecting: (state): boolean => state.status === "connecting",
    hasError: (state): boolean => state.error !== undefined,
  },

  actions: {
    setSession(sessionId: string, roomId: string, provider?: VideoProviderType, credentials?: any) {
      this.sessionId = sessionId;
      this.roomId = roomId;
      this.provider = provider;
      this.credentials = credentials;
      this.status = "initiating";
    },

    setStatus(status: CallStatus) {
      this.status = status;
      if (status === "connected" && !this.connectionTime) {
        this.connectionTime = Date.now();
      }
    },

    toggleVideo() {
      this.isVideoEnabled = !this.isVideoEnabled;
    },

    toggleAudio() {
      this.isAudioEnabled = !this.isAudioEnabled;
    },

    setError(code: string, message: string, details?: any) {
      this.error = { code, message, details };
      this.status = "failed";
    },

    clearError() {
      this.error = undefined;
    },

    endCall() {
      this.sessionId = null;
      this.roomId = null;
      this.initiatorId = null;
      this.status = "idle";
      this.connectionTime = undefined;
      this.error = undefined;
      this.provider = undefined;
      this.credentials = undefined;
    },

    setCredentials(credentials: any) {
      this.credentials = credentials;
    },

    reset() {
      this.$reset();
    },
  },
});
